import { useState } from "react";
import { Pencil, RotateCcw, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, safe } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const show = (l, v) => (v == null ? "-" : l.fmt === "pct" ? `${(v * 100).toFixed(2)}%` : `${v.toFixed(2)}x`);

function LimitRow({ task, l, reload, canEdit }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const save = safe(async (reset) => {
    const n = reset ? null : parseFloat(String(val).replace(/[%x]/gi, ""));
    if (!reset && Number.isNaN(n)) return toast.error("Enter a number");
    await api.put(`/tasks/${task.id}/limits/${l.metric}`, { value: reset ? null : l.fmt === "pct" ? n / 100 : n, reason: reset ? reason || "Reset to bank policy" : reason });
    setEdit(false); setReason(""); await reload();
    toast.success(reset ? `${l.label} reset to bank policy` : `${l.label} set for this loan`);
  });
  const open = () => { setVal(l.fmt === "pct" ? (l.value * 100).toFixed(2) : l.value.toFixed(2)); setEdit(true); };
  return (
    <div data-testid={`loan-limit-${l.metric}`} className="px-4 py-2.5 border-t border-slate-100 first:border-t-0">
      <div className="flex items-center gap-3">
        <span className="flex-1 text-xs text-slate-700">{l.label}</span>
        <span className="text-[10px] text-slate-400">Bank {show(l, l.bank_value)}</span>
        <span data-testid={`loan-limit-value-${l.metric}`} className={`num text-sm font-semibold ${l.override ? "text-amber-700" : "text-slate-900"}`}>{show(l, l.value)}</span>
        {l.override && <span data-testid={`loan-limit-override-${l.metric}`} className="chip border-0 ring-1 ring-amber-200 bg-amber-50 text-amber-700">Loan-level</span>}
        {canEdit && !edit && <button data-testid={`loan-limit-edit-${l.metric}`} onClick={open} className="text-slate-400 hover:text-sky-700" title="Edit for this loan"><Pencil className="w-3.5 h-3.5" /></button>}
        {canEdit && !edit && l.override && <button data-testid={`loan-limit-reset-${l.metric}`} onClick={() => save(true)} className="text-slate-400 hover:text-sky-700" title="Reset to bank policy"><RotateCcw className="w-3.5 h-3.5" /></button>}
      </div>
      {l.override && <div className="text-[10px] text-slate-500 mt-0.5">{l.override.by}: {l.override.reason}</div>}
      {edit && (
        <div className="flex flex-wrap gap-2 mt-2">
          <input data-testid={`loan-limit-input-${l.metric}`} value={val} onChange={(e) => setVal(e.target.value)} className="w-24 num text-xs px-2 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-sky-400" />
          <span className="text-[11px] text-slate-400 self-center">{l.fmt === "pct" ? "%" : "x"} · allowed {show(l, l.min)} to {show(l, l.max)}</span>
          <input data-testid={`loan-limit-reason-${l.metric}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="flex-1 min-w-[180px] text-xs px-2 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-sky-400" />
          <button data-testid={`loan-limit-save-${l.metric}`} disabled={reason.trim().length < 5} onClick={() => save(false)} className="btn-primary disabled:opacity-40">Apply</button>
          <button onClick={() => setEdit(false)} className="btn-ghost">Cancel</button>
        </div>
      )}
    </div>
  );
}

export function LoanLimits({ task, reload }) {
  const { persona } = useApp();
  const decided = ["Approve", "Approve with conditions", "Decline"].includes(task.decisions?.[task.decisions.length - 1]?.decision);
  const canEdit = persona?.permissions.includes("lending.edit") && !decided;
  if (!task.loan_limits) return null;
  return (
    <div className="panel" data-testid="loan-limits">
      <div className="px-4 py-3 border-b border-slate-200 flex justify-between items-center">
        <span className="eyebrow flex items-center gap-1.5"><SlidersHorizontal className="w-3 h-3" />Policy limits for this loan</span>
        <span className="text-[10px] text-slate-500">{task.policy_version} · {task.loan.property_class} defaults{decided ? " · locked after decision" : ""}</span>
      </div>
      {task.loan_limits.map((l) => <LimitRow key={l.metric} task={task} l={l} reload={reload} canEdit={canEdit} />)}
    </div>
  );
}
